import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Copy } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { name: "", email: "", subject: "", priority: "normal", message: "" };
const field = "h-12 rounded-xl border-slate-300 bg-white text-slate-900 placeholder:text-slate-400";

export const TicketForm = ({ onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/tickets", f);
      setCreated({ code: data.code, email: f.email });
      localStorage.setItem("sate_last_ticket", JSON.stringify({ code: data.code, email: f.email }));
      setF(EMPTY);
      toast.success(`Ticket ${data.code} created`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <div className="rounded-2xl border border-blue-200 bg-blue-50 p-8 text-center" data-testid="ticket-created">
        <p className="text-sm font-semibold text-slate-700">Your ticket number</p>
        <button onClick={() => { navigator.clipboard?.writeText(created.code); toast.success("Copied"); }} data-testid="ticket-created-code" className="font-display mx-auto mt-2 flex items-center gap-3 text-4xl font-black text-slate-900">{created.code}<Copy className="h-5 w-5 text-blue-600" /></button>
        <p className="mt-4 text-sm text-slate-600">Save it — you'll need it with your email to track replies.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button onClick={() => onCreated?.(created)} data-testid="ticket-track-now-button" className="rounded-full bg-blue-600 px-5 py-2.5 text-sm font-bold text-white transition-transform hover:-translate-y-0.5">Track this ticket</button>
          <button onClick={() => setCreated(null)} data-testid="ticket-new-button" className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-900">Open another</button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="ticket-submit-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Input required placeholder="Your name" value={f.name} onChange={set("name")} className={field} data-testid="ticket-name-input" />
        <Input required type="email" placeholder="Email address" value={f.email} onChange={set("email")} className={field} data-testid="ticket-email-input" />
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <Input required placeholder="Subject" value={f.subject} onChange={set("subject")} className={`${field} sm:col-span-2`} data-testid="ticket-subject-input" />
        <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
          <SelectTrigger className={field} data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["low", "normal", "high", "urgent"].map((p) => <SelectItem key={p} value={p} data-testid={`ticket-priority-${p}`} className="capitalize">{p[0].toUpperCase() + p.slice(1)} priority</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Textarea required rows={5} placeholder="How can we help?" value={f.message} onChange={set("message")} className="rounded-xl border-slate-300 bg-white text-slate-900 placeholder:text-slate-400" data-testid="ticket-message-input" />
      <button disabled={busy} data-testid="ticket-submit-button" className="font-display inline-flex w-full items-center justify-center gap-2 rounded-full bg-blue-600 px-6 py-4 text-sm font-bold uppercase tracking-wide text-white transition-[transform,background-color] hover:-translate-y-0.5 hover:bg-blue-700 disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit ticket
      </button>
    </form>
  );
};
